import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { CloseIcon, DescriptionIcon } from '@wf-sl/common-icons'
import { Button } from '@wf-common/atoms'

import styles from './info-box.module.css'

InfoBox.propTypes = {
    className: PropTypes.string,
    closeButtonClassName: PropTypes.string,
    closeIconClassName: PropTypes.string,
    content: PropTypes.node.isRequired,
    contentClassName: PropTypes.string,
    contentWrapperClassName: PropTypes.string,
    icon: PropTypes.node,
    iconWrapperClassName: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    visible: PropTypes.bool
}

InfoBox.defaultProps = {
    icon: <DescriptionIcon width={32} inheritColor />,
    visible: true
}

/**
 * A component which renders a closable info box. It contains of an optional icon, a close button
 * which triggers the passed onClose function and the actual content. The visible property determines,
 * whether the info box is shown or not. By default the InfoBox uses the DescriptionIcon as icon.
 * If the icon property is set to null, no icon is rendered at all.
 * All containers inside this component can be styled via dedicated className properties.
 *
 * @param {Object} props
 * @param {Function} props.onClose - a callback called when the close button is clicked
 * @param {React.Component} [props.icon] - a component which is used as the icon; null removes the icon
 * @param {string} props.className - the className to be appended to the root div
 * @param {string} props.closeButtonClassName - the className to be appended to the close button
 * @param {string} props.closeIconClassName - the className to be appended to the close icon
 * @param {string} props.contentClassName - the className to be appended to the content div
 * @param {string} props.contentWrapperClassName - the className to be appended to the content wrapper
 * @param {string} props.iconWrapperClassName - the className to be appended to the root div
 * @param {boolean} [props.visible] - determines, whether the box is rendered; defaults to true
 *
 * @return {React.Component}
 */
function InfoBox(props) {
    const {
        className,
        closeButtonClassName,
        closeIconClassName,
        content,
        contentClassName,
        contentWrapperClassName,
        icon,
        iconWrapperClassName,
        onClose,
        visible
    } = props

    return visible ? (
        <div className={cx(styles['info-box'], className, 't3sel-info-box')}>
            <Button
                icon
                onClick={onClose}
                className={cx(styles['close-button'], closeButtonClassName, 't3sel-info-box-close-button')}
            >
                <CloseIcon className={cx(styles['close-icon'], closeIconClassName)} fillParent />
            </Button>

            <div className={cx(styles['content-wrapper'], contentWrapperClassName, 't3sel-info-box-content-wrapper')}>
                {icon && (
                    <div className={cx(styles['icon'], iconWrapperClassName, 't3sel-info-box-icon-wrapper')}>
                        {icon}
                    </div>
                )}

                <div className={cx(contentClassName, 't3sel-info-box-content')}>{content}</div>
            </div>
        </div>
    ) : null
}

export { InfoBox }
